import { Component } from '@theme/component';
import { BundleUpdateEvent, QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * Bundle / quantity-tier selector.
 *
 * Each tier is a `.bundle-option` carrying `data-bundle-quantity`. Selecting a
 * tier drives the product form's existing `quantity-selector-component` via its
 * public `setValue()` API and dispatches a `QuantitySelectorUpdateEvent` so the
 * sticky add-to-cart stays in sync.
 *
 * When the product has a color option, the panel shows one
 * `.bundle-colors__row` per unit for every tier (rows default to different
 * colors). Row 1 forwards its choice to the main picker's color circles so
 * gallery/price/URL stay in sync — without moving the other rows. Only a
 * DIRECT click on the picker's circles re-derives untouched rows (cycling
 * color offset).
 * On submit, `ProductFormComponent.handleSubmit` asks this component (via the
 * public `getBundleItems()`) for grouped line items and adds them in one
 * batch; for a single unit the regular single-variant submission runs.
 * Tier/color changes are broadcast as `BundleUpdateEvent`s so the sticky
 * add-to-cart bar can mirror the bundle summary and price.
 *
 * @typedef {object} BundleSelectorRefs
 * @property {HTMLElement} [colorPanel] - The per-unit color panel.
 * @property {HTMLElement[]} [colorRows] - One row per bundle unit.
 *
 * @extends {Component<BundleSelectorRefs>}
 */
class BundleSelectorComponent extends Component {
  #abortController = new AbortController();

  /**
   * True while row 1 forwards its swatch to the main picker. The picker's
   * `change` re-enters #onSectionChange, which would re-derive the OTHER
   * untouched rows — picking Fan I must never move Fan II/III.
   * @type {boolean}
   */
  #forwardingRowColor = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.addEventListener('click', this.#onActivate, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });

    const section = this.closest('.shopify-section');

    // Follow the main variant picker's color for rows the buyer hasn't touched.
    section?.addEventListener('change', this.#onSectionChange, { signal });

    // Sync the quantity to the pre-selected tier once the quantity selector has upgraded.
    customElements.whenDefined('quantity-selector-component').then(() => {
      const selected = this.#selectedOption() ?? this.#firstOption();
      if (selected) this.#syncQuantity(selected);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /** @param {Event} event */
  #onActivate = (event) => {
    if (!(event.target instanceof Element)) return;

    const swatch = /** @type {HTMLButtonElement | null} */ (event.target.closest('.bundle-colors__swatch'));
    if (swatch && this.contains(swatch)) {
      this.#selectSwatch(swatch);
      return;
    }

    const option = this.#optionFrom(event.target);
    if (option) this.#select(option);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    const option = this.#optionFrom(event.target);
    if (!option) return;
    event.preventDefault();
    this.#select(option);
  };

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #optionFrom(target) {
    if (!(target instanceof Element)) return null;
    const option = /** @type {HTMLElement | null} */ (target.closest('.bundle-option'));
    return option && this.contains(option) ? option : null;
  }

  /** @returns {HTMLElement | null} */
  #selectedOption() {
    return this.querySelector('.bundle-option[aria-checked="true"]');
  }

  /** @returns {HTMLElement | null} */
  #firstOption() {
    return this.querySelector('.bundle-option');
  }

  /** @param {HTMLElement} option */
  #select(option) {
    for (const el of this.querySelectorAll('.bundle-option')) {
      const selected = el === option;
      el.classList.toggle('bundle-option--selected', selected);
      el.setAttribute('aria-checked', selected ? 'true' : 'false');
    }
    this.#syncQuantity(option);
    this.#revealBuyButtons();
  }

  /**
   * After a user picks a tier on mobile, nudge the page just enough to bring
   * the Buy now / Add to cart buttons fully into view — but only when they're
   * currently clipped by the fold. Adding units grows the color panel and
   * pushes the buttons down, so measure AFTER #syncQuantity has reflowed.
   * No-op on desktop (that add flow leaves the page for /cart) and whenever
   * the buttons already sit inside the viewport.
   */
  #revealBuyButtons() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const scope = this.closest('.shopify-section') ?? document;
    // Scope to the main buy-buttons block: the `.add-to-cart-button` class is
    // also worn by the (hidden, zero-box) sticky bar and quick-add buttons in
    // the same section — a 0×0 rect would poison the region bounds. Keep only
    // laid-out elements.
    const buyBlock = scope.querySelector('.buy-buttons-block');
    if (!buyBlock) return;
    const rects = [buyBlock.querySelector('.buy-now-button'), buyBlock.querySelector('.add-to-cart-button')]
      .map((el) => el?.getBoundingClientRect())
      .filter(/** @returns {r is DOMRect} */ (r) => r != null && r.height > 0);
    if (!rects.length) return;
    const regionTop = Math.min(...rects.map((r) => r.top));
    const regionBottom = Math.max(...rects.map((r) => r.bottom));

    const margin = 16;
    const viewport = window.innerHeight;

    let delta = 0;
    if (regionBottom > viewport - margin) {
      // Clipped below the fold — scroll down just enough to clear the bottom,
      // but never so far that the region's top slips above the top edge.
      delta = Math.min(regionBottom - viewport + margin, Math.max(0, regionTop - margin));
    } else if (regionTop < margin) {
      // Clipped above — scroll up so the region's top clears the top edge.
      delta = regionTop - margin;
    }

    if (Math.abs(delta) < 1) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollBy({ top: delta, left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** @param {HTMLElement} option */
  #syncQuantity(option) {
    const quantity = parseInt(option.dataset.bundleQuantity || '1', 10) || 1;
    const scope = this.closest('.shopify-section') ?? document;
    const quantitySelector = /** @type {any} */ (scope.querySelector('quantity-selector-component'));
    if (quantitySelector?.setValue) {
      quantitySelector.setValue(String(quantity));
      quantitySelector.dispatchEvent(new QuantitySelectorUpdateEvent(quantity));
    }
    this.#syncRows(quantity);
  }

  /**
   * Shows one color row per bundle unit (the panel is always visible; a single
   * unit shows one row, kept in sync with the main picker's circles).
   * @param {number} quantity
   */
  #syncRows(quantity) {
    const { colorPanel, colorRows } = this.refs;
    if (!colorPanel || !colorRows?.length) {
      this.#notifyBundleUpdate();
      return;
    }

    colorRows.forEach((row, index) => {
      row.hidden = index >= quantity;
    });
    this.#notifyBundleUpdate();
  }

  /** @param {HTMLButtonElement} swatch */
  #selectSwatch(swatch) {
    const row = swatch.closest('.bundle-colors__row');
    if (!(row instanceof HTMLElement)) return;

    row.dataset.touched = 'true';
    for (const el of row.querySelectorAll('.bundle-colors__swatch')) {
      el.setAttribute('aria-checked', el === swatch ? 'true' : 'false');
    }

    // The first unit doubles as the page-level color: forward it to the hidden
    // main variant picker so the gallery, price, and availability stay in sync.
    if (row === this.refs.colorRows?.[0]) {
      this.#syncMainPicker(swatch.dataset.colorName);
    }

    this.#notifyBundleUpdate();
  }

  /**
   * Checks the main variant picker's radio for the given color, if not already.
   * @param {string | undefined} colorName
   */
  #syncMainPicker(colorName) {
    const optionName = this.refs.colorPanel?.dataset.optionName;
    if (!optionName || !colorName) return;

    const scope = this.closest('.shopify-section') ?? document;
    for (const input of scope.querySelectorAll('variant-picker input[type="radio"]')) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.optionName === optionName &&
        input.value === colorName
      ) {
        if (!input.checked) {
          // click() fires `change` synchronously — flag it so #onSectionChange
          // leaves the other rows alone (gallery/price still update normally).
          this.#forwardingRowColor = true;
          try {
            input.click();
          } finally {
            this.#forwardingRowColor = false;
          }
        }
        return;
      }
    }
  }

  /**
   * Keeps untouched rows in sync when the buyer changes color in the main
   * variant picker. Rows keep the "different colors by default" pattern: row n
   * follows the picker's color offset by n-1 steps (cycling), so a 2-pack
   * stays e.g. Green + Black after switching the picker to Green.
   * @param {Event} event
   */
  #onSectionChange = (event) => {
    // Change originated from row 1's forwarded swatch click — the other rows
    // must not be re-derived (picking Fan I must never move Fan II/III).
    if (this.#forwardingRowColor) return;

    const { colorPanel, colorRows } = this.refs;
    const input = event.target;
    if (!colorPanel || !colorRows?.length || !(input instanceof HTMLInputElement)) return;
    if (!input.closest('variant-picker') || input.dataset.optionName !== colorPanel.dataset.optionName) return;

    colorRows.forEach((row, rowIndex) => {
      if (row.dataset.touched === 'true') return;
      const swatches = /** @type {HTMLElement[]} */ ([...row.querySelectorAll('.bundle-colors__swatch')]);
      const baseIndex = swatches.findIndex((el) => el.dataset.colorName === input.value);
      if (baseIndex === -1) return;

      let targetIndex = (baseIndex + rowIndex) % swatches.length;
      if (swatches[targetIndex]?.hasAttribute('disabled')) targetIndex = baseIndex;

      swatches.forEach((el, i) => {
        el.setAttribute('aria-checked', i === targetIndex && !el.hasAttribute('disabled') ? 'true' : 'false');
      });
    });
    this.#notifyBundleUpdate();
  };

  /**
   * Collects the per-unit selections as cart line items, or null when the
   * regular single-variant submission should run instead (single unit: row 1
   * already drives the main picker, so the default form flow is correct).
   * Called by `ProductFormComponent.handleSubmit` before its default submission.
   * @returns {Array<{variantId: string, quantity: number}> | null}
   */
  getBundleItems() {
    const rows = this.#visibleRows();
    if (rows.length < 2) return null;

    /** @type {Map<string, number>} */
    const counts = new Map();
    for (const row of rows) {
      const checked = /** @type {HTMLElement | null} */ (row.querySelector('.bundle-colors__swatch[aria-checked="true"]'));
      const variantId = checked?.dataset.variantId;
      if (!variantId) return null;
      counts.set(variantId, (counts.get(variantId) ?? 0) + 1);
    }
    if (counts.size === 0) return null;

    return Array.from(counts, ([variantId, quantity]) => ({ variantId, quantity }));
  }

  /** @returns {HTMLElement[]} */
  #visibleRows() {
    return (this.refs.colorRows ?? []).filter((row) => !row.hidden);
  }

  /**
   * Current bundle display state for the sticky add-to-cart bar. Also pulled
   * directly by sticky-add-to-cart.js on connect, in case it upgrades after
   * the initial BundleUpdateEvent was dispatched.
   * @returns {{quantity: number, summary: string | null, price: string | null, compare: string | null}}
   */
  getStickyDisplay() {
    const selected = this.#selectedOption();
    const quantity = parseInt(selected?.dataset.bundleQuantity || '1', 10) || 1;

    /** @type {string | null} */
    let summary = null;
    /** @type {string | null} */
    let price = null;
    /** @type {string | null} */
    let compare = null;

    if (quantity >= 2) {
      price = selected?.dataset.tierTotal ?? null;
      compare = selected?.dataset.tierWas ?? null;

      /** @type {Map<string, number>} */
      const counts = new Map();
      for (const row of this.#visibleRows()) {
        const checked = /** @type {HTMLElement | null} */ (row.querySelector('.bundle-colors__swatch[aria-checked="true"]'));
        const name = checked?.dataset.colorName;
        if (!name) {
          counts.clear();
          break;
        }
        counts.set(name, (counts.get(name) ?? 0) + 1);
      }
      if (counts.size > 0) {
        summary = Array.from(counts, ([name, count]) => `${count}× ${name}`).join(' / ');
      }
    }

    return { quantity, summary, price, compare };
  }

  #notifyBundleUpdate() {
    document.dispatchEvent(new BundleUpdateEvent(this.getStickyDisplay()));
  }

}

if (!customElements.get('bundle-selector-component')) {
  customElements.define('bundle-selector-component', BundleSelectorComponent);
}
